export type GoogleIdTokenClaims = {
  sub: string;
  email: string;
  name?: string;
  given_name?: string;
  family_name?: string;
};

/** Decode the payload of a Google Sign-In ID token (JWT) without external dependencies. */
export function parseGoogleIdToken(credential: string): GoogleIdTokenClaims | null {
  if (!credential || typeof credential !== 'string') return null;

  try {
    const parts = credential.split('.');
    if (parts.length !== 3) return null;

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = JSON.parse(atob(padded));

    if (!json?.sub || !json?.email) return null;

    return {
      sub: String(json.sub),
      email: String(json.email),
      name: json.name ? String(json.name) : undefined,
      given_name: json.given_name ? String(json.given_name) : undefined,
      family_name: json.family_name ? String(json.family_name) : undefined,
    };
  } catch {
    return null;
  }
}

/** Shape consumed by SocialLogin / buildSocialPayload after Google auth. */
export function googleCredentialToProfile(credential: string): {
  sub: string;
  email: string;
  name: string;
  access_token: string;
} | null {
  const claims = parseGoogleIdToken(credential);
  if (!claims) return null;

  const name =
    claims.name ||
    [claims.given_name, claims.family_name].filter(Boolean).join(' ') ||
    claims.email;

  return {
    sub: claims.sub,
    email: claims.email,
    name,
    /** Keeps existing Google auth gate in SocialLogin; value is the ID token, not an OAuth access token. */
    access_token: credential,
  };
}
